/**
 * Apple Sign In Controller
 *
 * Handles the Sign In with Apple flow using Apple's JavaScript SDK.
 * When clicked, opens Apple's sign-in UI, then submits the ID token
 * and user data to the backend for validation.
 *
 * Usage:
 * <div data-controller="apple-signin"
 *      data-apple-signin-client-id-value="com.jenie.web"
 *      data-apple-signin-redirect-uri-value="https://jenie.com/auth/social/callback/"
 *      data-apple-signin-callback-url-value="/auth/social/callback/">
 *   <button data-action="click->apple-signin#signIn">
 *     Continue with Apple
 *   </button>
 * </div>
 */

import ApplicationController from './application_controller';

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleAuthConfig) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

interface AppleAuthConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  usePopup: boolean;
}

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

export default class extends ApplicationController {
  static values = {
    clientId: String,
    redirectUri: String,
    callbackUrl: { type: String, default: '/auth/social/callback/' },
    nextUrl: { type: String, default: '' },
  };

  declare clientIdValue: string;
  declare redirectUriValue: string;
  declare callbackUrlValue: string;
  declare nextUrlValue: string;

  private sdkLoaded: boolean = false;
  private sdkLoading: boolean = false;

  connect(): void {
    if (!this.clientIdValue) {
      console.warn('Apple Sign In: Missing client ID');
      return;
    }
    this.loadAppleSDK();
  }

  /**
   * Load the Apple Sign In SDK
   */
  private loadAppleSDK(): void {
    if (this.sdkLoaded || this.sdkLoading) return;

    // Check if SDK is already loaded
    if (window.AppleID?.auth) {
      this.sdkLoaded = true;
      this.initializeApple();
      return;
    }

    this.sdkLoading = true;

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.sdkLoaded = true;
      this.sdkLoading = false;
      this.initializeApple();
    };
    script.onerror = () => {
      this.sdkLoading = false;
      console.error('Apple Sign In: Failed to load SDK');
    };

    document.head.appendChild(script);
  }

  /**
   * Initialize Apple Sign In SDK
   */
  private initializeApple(): void {
    if (!window.AppleID?.auth) {
      console.error('Apple Sign In: SDK not available');
      return;
    }

    window.AppleID.auth.init({
      clientId: this.clientIdValue,
      scope: 'email name',
      redirectURI: this.redirectUriValue || window.location.origin + this.callbackUrlValue,
      usePopup: true,
    });
  }

  /**
   * Trigger Apple Sign In flow
   */
  async signIn(event?: Event): Promise<void> {
    if (event) event.preventDefault();

    if (!window.AppleID?.auth) {
      console.error('Apple Sign In: SDK not ready');
      return;
    }

    try {
      const response = await window.AppleID.auth.signIn();
      this.handleSignInResponse(response);
    } catch (error: unknown) {
      // User cancelled or error occurred
      if (error && typeof error === 'object' && 'error' in error) {
        const errorObj = error as { error: string };
        if (errorObj.error === 'popup_closed_by_user') {
          console.log('Apple Sign In: User cancelled');
          return;
        }
      }
      console.error('Apple Sign In error:', error);
    }
  }

  /**
   * Handle the sign-in response from Apple
   */
  private handleSignInResponse(response: AppleSignInResponse): void {
    const idToken = response.authorization?.id_token;

    if (!idToken) {
      console.error('Apple Sign In: No ID token received');
      return;
    }

    // Apple only provides user info on the first sign-in
    const userData = response.user || null;

    // Submit to backend
    this.submitToBackend(idToken, userData);
  }

  /**
   * Submit the ID token and user data to the backend
   */
  private submitToBackend(
    idToken: string,
    userData: AppleSignInResponse['user'] | null
  ): void {
    // Create a form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = this.callbackUrlValue;
    form.style.display = 'none';

    // Add CSRF token
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfmiddlewaretoken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add provider
    const providerInput = document.createElement('input');
    providerInput.type = 'hidden';
    providerInput.name = 'provider';
    providerInput.value = 'apple';
    form.appendChild(providerInput);

    // Add ID token
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'id_token';
    tokenInput.value = idToken;
    form.appendChild(tokenInput);

    // Add user data if present (first sign-in only)
    if (userData) {
      const userDataInput = document.createElement('input');
      userDataInput.type = 'hidden';
      userDataInput.name = 'user_data';
      userDataInput.value = JSON.stringify(userData);
      form.appendChild(userDataInput);
    }

    // Add next URL if present
    if (this.nextUrlValue) {
      const nextInput = document.createElement('input');
      nextInput.type = 'hidden';
      nextInput.name = 'next';
      nextInput.value = this.nextUrlValue;
      form.appendChild(nextInput);
    }

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Get CSRF token from cookies or meta tag
   */
  private getCSRFToken(): string | null {
    // Try meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    // Fall back to cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }

    return null;
  }
}
